import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack } from "react-bootstrap";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  signIn,
  fetchUserAttributes,
  SignInInput,
  fetchAuthSession,
  signOut,
} from "aws-amplify/auth";

import { useAppDispatch } from "../../redux/store";
import { Layout } from "../../layout/AuthLayout/AuthLayout";
import FormInput from "../../components/FormInput/FormInput";
import CustomButton from "../../components/CustomButton/CustomButton";
import { setAuthDetails } from "../../redux/slice/persist/authSlice";
import "./login.scss";

interface FormValues {
  email: string;
  password: string;
}

const LoginPage = () => {
  const [errorMessage, setErrorMessage] = useState("");

  const initialValues: FormValues = {
    email: "",
    password: "",
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Invalid email address"
      )
      .required("Email is required"),
    password: Yup.string()
      .min(12, "Password must be at least 12 characters long")
      .matches(
        /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/,
        "Password must contain at least one number, one symbol, and one upper-case letter"
      )
      .required("Password is required"),
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  let location = useLocation();
  let from = location.state?.from?.pathname || "/";

  const handleSubmit = async (values: { email: string; password: string }) => {
    setErrorMessage("");
    const signInInput: SignInInput = {
      username: values.email,
      password: values.password,
    };
    try {
      const signInOutput = await signIn(signInInput);

      if (signInOutput?.nextStep?.signInStep === "DONE") {
        // assume success

        // need to get more user info
        let firstName = "";
        let lastName = "";
        let role = "";
        try {
          const { accessToken } = (await fetchAuthSession()).tokens || {};

          const cognitoGroups: string[] = accessToken?.payload?.[
            "cognito:groups"
          ] as string[];
          const isWlcAdminGroupMember = cognitoGroups?.includes("wlcadmin");

          if (!isWlcAdminGroupMember) {
            setErrorMessage(
              "Permission Denied. User is not a WLC CPaaS Admin Group Member"
            );
            await signOut();
            return;
          }
          const fetchUserAttributesOutput = await fetchUserAttributes();

          firstName = fetchUserAttributesOutput.given_name ?? "";
          lastName = fetchUserAttributesOutput.family_name ?? "";
          role = fetchUserAttributesOutput["custom:cpaastype"] ?? "";
        } catch (error) {
          let message = "Unknown Error";
          if (error instanceof Error) message = error.message;
          setErrorMessage(message);
        }

        dispatch(
          setAuthDetails({
            isLoggedIn: true,
            email: values.email,
            firstName: firstName,
            lastName: lastName,
            role: role,
          })
        );

        from !== "/logout" ? navigate(from, { replace: true }) : navigate("/");
      } else {
        // display error message and stay on the page
        let message =
          "unknown signUpStep after confirmSignUp: " +
          signInOutput?.nextStep?.signInStep;
        console.log(message);
        setErrorMessage(message);
      }
    } catch (error) {
      console.log("error signing in");
      console.log(error);

      let message = "Unknown Error";
      if (error instanceof Error) message = error.message;
      setErrorMessage(message);
    }
  };

  return (
    <Layout>
      <Stack className="mx-auto">
        <h1 className="header-text">Administration</h1>
        <p
          className="text-center"
          style={{ fontWeight: 900, fontSize: "18px" }}
        >
          Log into your account
        </p>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            isSubmitting,
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
          }) => {
            const disabled = !Object.values(values).every(
              (val) => val.length > 0
            );

            return (
              <Form noValidate>
                <FormInput
                  label="Email"
                  type="email"
                  name="email"
                  placeholder="Enter email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required={true}
                  isValid={touched.email && !errors.email}
                  isInvalid={touched.email && !!errors.email}
                  touched={touched.email}
                  error={errors.email}
                  autoFocus={true}
                />
                <FormInput
                  label="Password"
                  type="password"
                  name="password"
                  placeholder="Enter Password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required={true}
                  isValid={touched.password && !errors.password}
                  isInvalid={touched.password && !!errors.password}
                  touched={touched.password}
                  error={errors.password}
                />
                <CustomButton
                  label="Log In"
                  isSubmitting={isSubmitting}
                  type="submit"
                  disabled={disabled}
                  error={errorMessage}
                />
              </Form>
            );
          }}
        </Formik>
      </Stack>
    </Layout>
  );
};

export default LoginPage;
